// djoidLibraryModel — pure UI logic for the DJoid-inspired library + persistent player
// (canonical prompt 91). No rendering, no I/O: this is the testable brain the renderer's
// thin components consume. It reuses LibraryTrackRow from libraryModel so the library has a
// single row shape across the app.
//
// Safety boundaries (canonical): this model never triggers audio *download* and never drives
// hardware. The persistent player here is preview-only state (which track, playing flag,
// position) — actual playback of previewable audio is the renderer's concern, and protected
// audio is never fetched. Rights warnings are surfaced, never hidden.

import type { LibraryTrackRow, DJSEMRightsStateUI } from "./libraryModel";

// ---- Camelot wheel -------------------------------------------------------------------------
// The Camelot/Open-Key wheel is the DJ-standard harmonic mixing aid (image 2/6 show it).
// 12 hours × 2 lanes: "A" = minor (inner), "B" = major (outer). Compatible keys for a smooth
// mix are: same key, ±1 hour on the same lane, and the adjacent lane at the same hour.

/** Camelot wheel lane: "A" = minor (inner), "B" = major (outer). */
export type CamelotLane = "A" | "B";
/** A parsed Camelot wheel position: hour (1..12) + lane. */
export interface CamelotKey { hour: number; lane: CamelotLane; } // hour 1..12

/** Number of hours on the Camelot wheel. */
export const CAMELOT_HOURS = 12;

/**
 * Normalise an hour into 1..12 (wraps around the wheel).
 * @param hour - Hour value, possibly out of 1..12 range.
 * @returns The equivalent hour within 1..12.
 */
export function wrapHour(hour: number): number {
  const m = ((hour - 1) % CAMELOT_HOURS + CAMELOT_HOURS) % CAMELOT_HOURS;
  return m + 1;
}

/**
 * Parse a Camelot code like "8A" / "12B" into a CamelotKey, or null if malformed.
 * @param code - The Camelot code string.
 * @returns The parsed {@link CamelotKey}, or null if the code is malformed or out of range.
 */
export function parseCamelot(code: string): CamelotKey | null {
  const m = /^([0-9]{1,2})([AB])$/.exec(code.trim().toUpperCase());
  if (!m) return null;
  const hour = Number(m[1]);
  if (hour < 1 || hour > CAMELOT_HOURS) return null;
  return { hour, lane: m[2] as CamelotLane };
}

/**
 * Format a CamelotKey back into its code string (e.g. "8A").
 * @param key - The Camelot key to format.
 * @returns The Camelot code string.
 */
export function formatCamelot(key: CamelotKey): string {
  return `${key.hour}${key.lane}`;
}

/**
 * Keys that mix harmonically with the given key: itself, ±1 hour on the same lane, and the
 * same hour on the other lane. Returned as Camelot codes (stable order for tests/UI).
 * @param key - The reference Camelot key.
 * @returns Compatible Camelot codes, in stable order.
 */
export function compatibleKeys(key: CamelotKey): string[] {
  const other: CamelotLane = key.lane === "A" ? "B" : "A";
  const out = [
    { hour: key.hour, lane: key.lane },
    { hour: wrapHour(key.hour - 1), lane: key.lane },
    { hour: wrapHour(key.hour + 1), lane: key.lane },
    { hour: key.hour, lane: other },
  ];
  return out.map(formatCamelot);
}

/**
 * True if two Camelot codes are harmonically compatible (order-independent).
 * @param a - First Camelot code.
 * @param b - Second Camelot code.
 * @returns True if `b` is one of `a`'s {@link compatibleKeys} (or `a` is malformed → false).
 */
export function keysCompatible(a: string, b: string): boolean {
  const ka = parseCamelot(a);
  if (!ka) return false;
  return compatibleKeys(ka).includes(b.trim().toUpperCase());
}

// ---- Collection tabs -----------------------------------------------------------------------
// Top tabs (image 1/6): each open collection is a closable tab; one is active.

/** A closable top-level collection tab. */
export interface CollectionTab { id: string; label: string; }

/**
 * Close a collection tab, choosing a new active tab if the closed one was active.
 * @param tabs - Current open tabs.
 * @param activeId - Currently active tab id.
 * @param closeId - Id of the tab to close.
 * @returns The remaining tabs and the (possibly changed) active tab id.
 */
export function closeTab(tabs: CollectionTab[], activeId: string, closeId: string): { tabs: CollectionTab[]; activeId: string } {
  const idx = tabs.findIndex((t) => t.id === closeId);
  if (idx < 0) return { tabs, activeId };
  const next = tabs.filter((t) => t.id !== closeId);
  if (closeId !== activeId) return { tabs: next, activeId };
  if (next.length === 0) return { tabs: next, activeId: "" };
  // activate the neighbour that used to sit before the closed tab (or the new first)
  const neighbour = next[Math.max(0, idx - 1)];
  return { tabs: next, activeId: neighbour.id };
}

// ---- View mode (grid/table), persisted per collection --------------------------------------

/** Library display mode: card grid or dense table. */
export type ViewMode = "grid" | "table";

/**
 * Per-collection view-mode store. Pure: returns a new map, never mutates the input.
 * @param store - Current collectionId → mode map.
 * @param collectionId - Collection to update.
 * @param mode - The new view mode for that collection.
 * @returns A new store map with the update applied.
 */
export function setViewMode(store: Record<string, ViewMode>, collectionId: string, mode: ViewMode): Record<string, ViewMode> {
  return { ...store, [collectionId]: mode };
}
/**
 * Read a collection's view mode, defaulting to `"grid"` when unset.
 * @param store - The collectionId → mode map.
 * @param collectionId - Collection to look up.
 * @returns The stored mode, or `"grid"` if none is set.
 */
export function getViewMode(store: Record<string, ViewMode>, collectionId: string): ViewMode {
  return store[collectionId] ?? "grid";
}

// ---- Range + Camelot filters ---------------------------------------------------------------

/** An inclusive numeric range filter. */
export interface Range { min: number; max: number; }
/** DJoid-style library filter facets. */
export interface LibraryFilters {
  camelotKeys: string[];          // selected Camelot codes (empty = any)
  harmonicExpand: boolean;        // if true, a selected key also matches its compatible keys
  bpm?: Range;
  energy?: Range;                 // 1..10
  danceability?: Range;           // 1..10
  emotion?: Range;                // -1..1 (per DJoid emotion column)
  rightsStates?: DJSEMRightsStateUI[];
}

/** The default, no-filters-applied {@link LibraryFilters} state. */
export const EMPTY_FILTERS: LibraryFilters = { camelotKeys: [], harmonicExpand: false };

/**
 * Number of active filter facets (drives the "Filters N" badge in image 2).
 * @param f - Current filters.
 * @returns Count of facets that are actively constraining results.
 */
export function activeFilterCount(f: LibraryFilters): number {
  let n = 0;
  if (f.camelotKeys.length) n += 1;
  if (f.bpm) n += 1;
  if (f.energy) n += 1;
  if (f.danceability) n += 1;
  if (f.emotion) n += 1;
  if (f.rightsStates && f.rightsStates.length) n += 1;
  return n;
}

/**
 * Toggle a Camelot key in the selection (pure).
 * @param f - Current filters.
 * @param code - Camelot code to toggle.
 * @returns Updated filters with `code` added or removed from `camelotKeys`.
 */
export function toggleCamelotKey(f: LibraryFilters, code: string): LibraryFilters {
  const c = code.trim().toUpperCase();
  const has = f.camelotKeys.includes(c);
  return { ...f, camelotKeys: has ? f.camelotKeys.filter((k) => k !== c) : [...f.camelotKeys, c] };
}

function inRange(v: number | undefined, r?: Range): boolean {
  if (!r) return true;
  if (v === undefined) return false;
  return v >= r.min && v <= r.max;
}

/** True if the track's key satisfies the Camelot selection (with optional harmonic expansion). */
function keyMatches(trackKey: string | undefined, f: LibraryFilters): boolean {
  if (!f.camelotKeys.length) return true;
  if (!trackKey) return false;
  const tk = trackKey.trim().toUpperCase();
  if (f.camelotKeys.includes(tk)) return true;
  if (f.harmonicExpand) return f.camelotKeys.some((sel) => keysCompatible(sel, tk));
  return false;
}

/**
 * Apply the DJoid-style filters to library rows. Uses the row's `camelot` field when present,
 * falling back to `key`. Energy/danceability/emotion read optional numeric fields on the row.
 * @param rows - Rows to filter.
 * @param f - Filter facets to apply.
 * @returns The matching subset of `rows`.
 */
export function applyLibraryFilters(rows: DjoidTrackRow[], f: LibraryFilters): DjoidTrackRow[] {
  return rows.filter((r) => {
    if (!keyMatches(r.camelot ?? r.key, f)) return false;
    if (!inRange(r.bpm, f.bpm)) return false;
    if (!inRange(r.energy, f.energy)) return false;
    if (!inRange(r.danceability, f.danceability)) return false;
    if (!inRange(r.emotion, f.emotion)) return false;
    if (f.rightsStates && f.rightsStates.length && !f.rightsStates.includes(r.rightsState)) return false;
    return true;
  });
}

/** Row shape the DJoid library UI reads — extends the canonical LibraryTrackRow with the
 *  DJ-facing display fields shown in the reference screens (Camelot code, energy 1..10, etc.). */
export interface DjoidTrackRow extends LibraryTrackRow {
  camelot?: string;        // e.g. "8A" (harmonic display; `key` may hold a musical key name)
  danceability?: number;   // 1..10
  emotion?: number;        // -1..1
  label?: string;          // record label (results table column)
  releaseYear?: number;
  format?: string;         // e.g. "mp3"
  artworkUrl?: string;
}

// ---- Bulk selection ------------------------------------------------------------------------

/**
 * Toggle a single track's selection (pure).
 * @param selected - Current selection set.
 * @param id - Track id to toggle.
 * @returns A new set with `id` added or removed.
 */
export function toggleSelected(selected: Set<string>, id: string): Set<string> {
  const next = new Set(selected);
  if (next.has(id)) next.delete(id); else next.add(id);
  return next;
}
/**
 * Select every given id.
 * @param ids - Track ids to select.
 * @returns A set containing all `ids`.
 */
export function selectAll(ids: string[]): Set<string> {
  return new Set(ids);
}
/** @returns An empty selection set. */
export function clearSelection(): Set<string> {
  return new Set();
}
/**
 * The floating bulk-action bar is visible only when ≥1 track is selected.
 * @param selected - Current selection set.
 * @returns True if at least one track is selected.
 */
export function bulkBarVisible(selected: Set<string>): boolean {
  return selected.size > 0;
}

// ---- Collection stats (header chips + mini energy chart) -----------------------------------

/** Header-chip summary stats for a collection of tracks. */
export interface CollectionStats {
  trackCount: number;
  artistCount: number;
  totalDurationSec: number;
  bpmMin: number; bpmMax: number;
  energyMin: number; energyMax: number;
  danceMin: number; danceMax: number;
}

/**
 * Compute collection header stats (counts, duration, BPM/energy/danceability ranges).
 * @param rows - Tracks in the collection.
 * @returns The computed {@link CollectionStats}; all-zero/empty when `rows` is empty.
 */
export function computeCollectionStats(rows: DjoidTrackRow[]): CollectionStats {
  if (!rows.length) {
    return { trackCount: 0, artistCount: 0, totalDurationSec: 0, bpmMin: 0, bpmMax: 0, energyMin: 0, energyMax: 0, danceMin: 0, danceMax: 0 };
  }
  const artists = new Set(rows.map((r) => r.artist).filter(Boolean));
  const bpms = rows.map((r) => r.bpm).filter((n): n is number => typeof n === "number");
  const energies = rows.map((r) => r.energy).filter((n): n is number => typeof n === "number");
  const dances = rows.map((r) => r.danceability).filter((n): n is number => typeof n === "number");
  const range = (xs: number[]): [number, number] => xs.length ? [Math.min(...xs), Math.max(...xs)] : [0, 0];
  const [bpmMin, bpmMax] = range(bpms);
  const [energyMin, energyMax] = range(energies);
  const [danceMin, danceMax] = range(dances);
  return {
    trackCount: rows.length,
    artistCount: artists.size,
    totalDurationSec: rows.reduce((s, r) => s + (r.durationSec || 0), 0),
    bpmMin, bpmMax, energyMin, energyMax, danceMin, danceMax,
  };
}

/**
 * Build a small energy sparkline series (one point per track, clamped 0..1) for the mini
 * chart in the collection header. Kept normalised so the renderer just maps to pixel heights.
 * @param rows - Tracks in the collection.
 * @returns One normalised (0..1) energy value per row.
 */
export function miniEnergySeries(rows: DjoidTrackRow[]): number[] {
  return rows.map((r) => Math.max(0, Math.min(1, (r.energy ?? 0) / 10)));
}

// ---- Persistent bottom player --------------------------------------------------------------
// Preview-only. Survives route/tab changes (the renderer keeps this state above the router).
// No protected-audio download: `previewUrl` is expected to be a short, licensed preview or a
// user-owned local file; rights-restricted rows expose no preview.

/** State of the persistent, preview-only bottom player (survives route/tab changes). */
export interface PlayerState {
  trackId: string | null;
  playing: boolean;
  positionSec: number;
  durationSec: number;
}

/** The player's initial/reset state — nothing loaded. */
export const EMPTY_PLAYER: PlayerState = { trackId: null, playing: false, positionSec: 0, durationSec: 0 };

/**
 * Whether a row may be previewed. Rights-restricted / uncleared AI audio is preview-blocked.
 * @param row - The track row to check.
 * @returns True if the row's rights state allows preview.
 */
export function canPreview(row: DjoidTrackRow): boolean {
  return row.rightsState !== "restricted" && row.rightsState !== "ai_generated_uncleared";
}

/**
 * Load a track into the player (guards rights). Returns unchanged state if preview-blocked.
 * @param state - Current player state.
 * @param row - Track to load.
 * @returns The new player state, or the unchanged `state` if the row can't be previewed.
 */
export function loadTrack(state: PlayerState, row: DjoidTrackRow): PlayerState {
  if (!canPreview(row)) return state;
  return { trackId: row.id, playing: true, positionSec: 0, durationSec: row.durationSec ?? 0 };
}

/**
 * Toggle play/pause; a no-op if no track is loaded.
 * @param state - Current player state.
 * @returns The updated player state.
 */
export function togglePlay(state: PlayerState): PlayerState {
  if (!state.trackId) return state;
  return { ...state, playing: !state.playing };
}

/**
 * Seek to a position, clamped to `[0, durationSec]`.
 * @param state - Current player state.
 * @param sec - Target position in seconds.
 * @returns The updated player state with `positionSec` clamped.
 */
export function seekTo(state: PlayerState, sec: number): PlayerState {
  const clamped = Math.max(0, Math.min(state.durationSec || 0, sec));
  return { ...state, positionSec: clamped };
}
